/**
 * t494u: Plan a journey rules shared by the desktop planner, phone web and iOS.
 * - Departs defaults to today in UK time: 08:00 before eight, else the next quarter hour (never a time already gone).
 * - A stop's name and its updates channel follow the building/address picked until someone changes them by hand.
 * - Day/time windows and load details are optional and collapsed unless set.
 */
import { DateTime } from 'luxon';
import type { DeliveryChannels } from './types';

const ZONE = 'Europe/London';

/** Default departure, UK wall time. 23:50 rolls to 00:00 the next day rather than offering a time already passed. */
export function defaultDeparture(now: number = Date.now()): { day: string; time: string } {
  const t = DateTime.fromMillis(now).setZone(ZONE);
  if (t.hour < 8) return { day: t.toISODate()!, time: '08:00' };
  const q = t.startOf('minute').plus({ minutes: 15 - (t.minute % 15) });
  return { day: q.toISODate()!, time: q.toFormat('HH:mm') };
}

// ------------------------------------------------------------------ stop name: auto until typed
export interface AutoName { label: string; label_auto: boolean }
/** A building or address was picked: an auto (or empty) name follows it; a typed name stays. */
export function nameAfterPick(s: AutoName, picked: string): AutoName {
  return s.label_auto || !s.label.trim() ? { label: picked, label_auto: true } : { label: s.label, label_auto: false };
}
/** Typing in Stop name: typed text stays put; clearing the field turns auto-fill back on. */
export const nameTyped = (v: string): AutoName => ({ label: v, label_auto: !v.trim() });
/** The first line of an address ("3 Blakeley Brow, Raby Mere, …" → "3 Blakeley Brow"). */
export const firstLine = (address: string) => address.split(',')[0].trim();

// ------------------------------------------------------------------ updates channel
/** The channel a stop's updates go to by default: its building's own, else the workspace default, else none. */
export function deliveryChannelFor(cfg: DeliveryChannels | null | undefined, locationId: string | null | undefined): string | null {
  if (!cfg) return null;
  return (locationId ? cfg.buildings[locationId]?.channel?.id : null) ?? cfg.workspace.channel?.id ?? null;
}
/** Where a default came from, for the muted hint under the picker. */
export function deliveryChannelSource(cfg: DeliveryChannels | null | undefined, locationId: string | null | undefined): 'building' | 'workspace' | null {
  if (!cfg) return null;
  if (locationId && cfg.buildings[locationId]?.channel) return 'building';
  return cfg.workspace.channel ? 'workspace' : null;
}

// ------------------------------------------------------------------ optional window and load
const wall = (v: string) => DateTime.fromISO(v);
/** "Window: Thu 29 Oct, 09:00–12:00" from datetime-local values (wall time, no zone shift). */
export function windowSummary(from: string | null | undefined, to: string | null | undefined): string | null {
  const a = from ? wall(from) : null, b = to ? wall(to) : null;
  const d = (x: DateTime) => x.toFormat('ccc d LLL'), t = (x: DateTime) => x.toFormat('HH:mm');
  if (a?.isValid && b?.isValid) return a.hasSame(b, 'day') ? `Window: ${d(a)}, ${t(a)}–${t(b)}` : `Window: ${d(a)}, ${t(a)} – ${d(b)}, ${t(b)}`;
  if (a?.isValid) return `Window: from ${d(a)}, ${t(a)}`;
  if (b?.isValid) return `Window: by ${d(b)}, ${t(b)}`;
  return null;
}
export interface StopLoad { pallets_drop: number | null; pallets_collect: number | null; weight_kg: number | null }
const given = (v: number | null | undefined) => v != null && v !== 0;
/** Any load worth showing? Old stops saved as 0/0/0 count as no load details. */
export const hasLoad = (s: StopLoad) => given(s.pallets_drop) || given(s.pallets_collect) || given(s.weight_kg);
/** "Drop 2 pallets · Collect 1 · 450 kg" (zero or blank parts left out). */
export function loadSummary(s: StopLoad): string | null {
  const parts = [given(s.pallets_drop) ? `Drop ${s.pallets_drop} pallet${s.pallets_drop === 1 ? '' : 's'}` : null,
    given(s.pallets_collect) ? `Collect ${s.pallets_collect}` : null, given(s.weight_kg) ? `${s.weight_kg} kg` : null].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
/** A number box's value: blank → null (not given), otherwise the number typed (0 included). */
export const loadValue = (v: string): number | null => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Math.max(0, Math.round(Number(v))));
